<template>
    <v-layout row>
        <v-flex xs12 sm12>
            <v-card class="elevation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <v-btn dark icon title="Back to Settings" @click="$router.push('/settings/holidays')">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <help-link appId="settings" pageId="holidays" color="white"></help-link>
                    <v-btn v-if="hasAddRights" fab dark small color="primary" title="Add New Holiday" @click="showAdd">
                        <v-icon dark>add</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-progress-linear v-if="loading||loading_sync" indeterminate color="primary" height="2" class="mt-0"></v-progress-linear>
            </v-card>
            <v-alert v-if="error" :value="true" color="error" icon="error" outline class="mt-5">
                {{ errorMessage }}
            </v-alert> 
            <v-alert v-if="!loading" v-show="!hasHoliday&&!hasNewSystemGenerated" :value="true" color="info" icon="info" outline class="mt-5" dismissible>
                No data found.               
            </v-alert> 
            <v-alert v-if="loading_sync" :value="true" color="info" icon="info" outline class="mt-5">
                Sync in progress.               
            </v-alert>
            <v-alert v-if="hasEditRights&&hasNewSystemGenerated&&!loading_sync" :value="true" color="info" icon="info" outline class="mt-5">
                There are new system generated holidays for {{fiscal_period.fiscalYear}}. 
                <v-btn outline color="info" dark @click="viewNew" >
                    <v-icon dark>preview</v-icon>&nbsp;&nbsp;View&nbsp;
                </v-btn>
                <v-btn outline color="info" dark @click="syncNew">
                    <v-icon dark>sync</v-icon>&nbsp;&nbsp;Sync&nbsp;
                </v-btn>
            </v-alert>

            <v-card class="elevation-1 mt-5">
                <v-toolbar class="transparent" flat height="45">
                    <div :class="isMobile?'wp-50 mb-10':'wp-30 mb-10'">
                        <v-text-field v-if="holidays_list.length" v-model="search" @keyup="filterData" append-icon="search" label="Search holiday or date" single-line hide-details clearable></v-text-field>
                    </div>
                    <v-spacer></v-spacer>
                </v-toolbar>
                <v-progress-linear v-if="loading||loading_sync" indeterminate color="primary" height="2" class="mt-0"></v-progress-linear>
            </v-card>

            <v-expansion-panel v-for="(item,index) in holidays_months" :key="index" expand class="mt-2">
                <v-expansion-panel-content>
                    <template v-slot:header >
                        <div>
                            <span class="font-weight-bold primary--text">{{ item }}</span>
                            <span v-if="getHolidayByMonth(item).createdText" class="orange--text"> ({{getHolidayByMonth(item).createdText}})</span>
                            <span v-if="getHolidayByMonth(item).forReviewText" class="orange--text"> ({{getHolidayByMonth(item).forReviewText}})</span>
                            <span v-if="getHolidayByMonth(item).rejectedText" class="red--text">&nbsp; ({{getHolidayByMonth(item).rejectedText}})</span>
                            <span v-if="getHolidayByMonth(item).approvedText" class="green--text">&nbsp; ({{getHolidayByMonth(item).approvedText}})</span>
                        </div>
                        <v-spacer></v-spacer>
                    </template>
                    <v-data-table
                        :headers="isMobile ? [] : headers"
                        :items="getHolidayByMonth(item).data"
                        :hide-headers="isMobile"
                        hide-actions
                    >
                        <template v-slot:items="props">

                            <!-- mobile view -->
                            <tr v-if="isMobile">
                                <td :colspan="headers.length" class="pa-1">
                                    <v-card class="elevation-1" @click="viewDetails(props.item)">
                                        <v-toolbar flat height="42">
                                            <v-chip class="user-chip-container font-weight-bold">
                                                {{ props.item.date | moment("YYYY-MM-DD (ddd)") }}
                                            </v-chip>
                                            <v-spacer></v-spacer>
                                        </v-toolbar>
                                        <v-layout row wrap align-center class="section-layout">
                                            <v-flex xs12 class="text-xs-left mt-5">
                                                <v-layout row>
                                                    <v-flex xs6>
                                                        <div class="caption grey--text text--darken-1">Name</div>
                                                        <div>{{ props.item.description }}</div>
                                                    </v-flex>
                                                    <v-flex xs6>
                                                        <div class="caption grey--text text--darken-1">Type</div>
                                                        <div>{{ props.item.holidayTypeName }}</div>
                                                    </v-flex>
                                                </v-layout>
                                                <v-layout row>
                                                    <v-flex xs6>
                                                        <div class="caption grey--text text--darken-1">Status</div>
                                                        <div :class="commonfn.getStatusColor(props.item.status)">{{commonfn.getStatusDescription(props.item.status)}}</div>
                                                    </v-flex>
                                                    <v-flex xs6>
                                                        <div class="caption grey--text text--darken-1">Created By</div>
                                                        <v-chip small class="user-chip-container">
                                                            <v-avatar>
                                                                <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(props.item.created.userId)"></v-img>
                                                            </v-avatar>
                                                            {{props.item.created.firstName}} {{props.item.created.lastName}}
                                                        </v-chip>
                                                    </v-flex>
                                                </v-layout>
                                            </v-flex>
                                        </v-layout>
                                    </v-card>
                                </td>
                            </tr>
                            
                            <!-- desktop view -->
                            <tr v-else @click="viewDetails(props.item)" title="Click to view details" class="clickable">
                                <td>{{ props.item.date | moment("YYYY-MM-DD (ddd)") }}</td>
                                <td>{{ props.item.description }}</td>
                                <td>{{ props.item.holidayTypeName }}</td>
                                <td>
                                    <b :class="commonfn.getStatusColor(props.item.status)">{{commonfn.getStatusDescription(props.item.status)}}</b>
                                </td>
                                <td>
                                    <span v-if="props.item.created.userId==0">System</span>
                                    <span v-else>
                                        <v-chip small class="user-chip-container">
                                            <v-avatar>
                                                <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(props.item.created.userId)"></v-img>
                                            </v-avatar>
                                            {{props.item.created.firstName}} {{props.item.created.lastName}}
                                        </v-chip>
                                    </span>
                                </td>
                            </tr>

                        </template>
                    </v-data-table> 
                </v-expansion-panel-content>
            </v-expansion-panel>
            
            <!-- Comments and Updates -->
            <v-layout row wrap>
                <v-flex xs12 sm6>
                    <!-- comments history component -->
                    <comments-history v-if="!loading" :comments_data="commentsData"></comments-history>
                </v-flex>
                <v-flex xs12 sm6>
                    <update-history v-if="!loading" ref="updateHistoryRef" :page_query_id="pageQueryId"></update-history>
                </v-flex>
            </v-layout>
        </v-flex>

        <v-dialog v-model="dialogAddEdit" persistent width="600">
            <addedit-holiday ref="addEditHolidayRef" :dialogAddEdit.sync="dialogAddEdit" @send="getHolidays"></addedit-holiday>
        </v-dialog>
        <v-dialog v-model="dialogNew" persistent width="700">
            <new-holidays ref="newHolidayRef" :dialogNew.sync="dialogNew" @send="syncHandler"></new-holidays>
        </v-dialog>

        <!-- status messaging component -->
        <status-messaging :page_query_id="pageQueryId" @refresh="updateHandler"></status-messaging>
    </v-layout>  
</template>

<script>

import holidaysApi from '@/api/holidays'
import commonfn from '@/common/functions'
import pageKeys from '@/common/pagekeys'
import notificationTypes from '@/common/notificationTypes'
import awsS3Helper from '@/helpers/aws-s3'
import { hasAddRights, hasEditRights } from '@/helpers/access-permissions'
import { getSearchKeyword, updateSearchKeyword } from '@/helpers/user-prefs'

export default { 
    name: 'Holidays',
    metaInfo: {
        title: 'Holidays',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'View and create holdays, so that all users can view in Team Calendar' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
        ]
    },    
    components: {
        'help-link': () => import('@/components/Common/HelpLink'),
        'addedit-holiday' : () => import('@/components/Holidays/AddEditHoliday'),
        'new-holidays' : () => import('@/components/Holidays/NewSystemGenerated'),
        'status-messaging': () => import('@/components/Common/StatusMessaging'),
        'update-history': () => import('@/components/Common/UpdatesHistory'),
        'comments-history': () => import('@/components/Common/CommentsHistory')
    },   
    data () {
        return {
            commonfn,
            awsS3Helper,
            search: getSearchKeyword(this.$route.name),
            error: false,
            errorMessage: '',
            loading: false,
            loading_sync: false,
            loggedInTeamId: null,
            disabled: false,
            panel: [true],
            holidays_list: [],
            holidays_list_filtered: [],
            holidays_months: [],
            holiday_types: [
                {type: 1, Description: 'Regular'},
                {type: 2, Description: 'Special Non-Working'},
                {type: 3, Description: 'Special Working'},
            ],
            dialogAddEdit: false,
            dialogNew: false,
            mode: '',
            expand: false,
            headers: [
                {text: 'Holiday Date',align: 'left',value: '',width: '15%', sortable:false},
                {text: 'Holiday Name',align: 'left',value: '',width: '25%', sortable:false},
                {text: 'Holiday Type',align: 'left',value: '',width: '25%', sortable:false},
                {text: 'Status',align: 'left',value: '',width: '10%', sortable:false},
                {text: 'Created By',align: 'left',value: '',width: '10%', sortable:false},
            ],
            deleted: [],
            deleteLoading: false,
            toggle_view: 2,
            holidays_new: [],
            fiscal_period: {},
        }
    },
    methods: {   
        getHolidayByMonth(monthYear) {
            const data = this.holidays_list_filtered.filter(item => {
                return this.$moment(item.date).format('MMMM YYYY') == monthYear
            })
            const created = data.filter(item => {
                return item.status == commonfn.getStatusCode('Created')
            })
            const forReview = data.filter(item => {
                return item.status == commonfn.getStatusCode('For Review')
            })
            const rejected = data.filter(item => {
                return item.status == commonfn.getStatusCode('Rejected')
            })
            const approved = data.filter(item => {
                return item.status == commonfn.getStatusCode('Approved')
            })

            const createdText = created.length ? `${created.length} Created`:''
            const forReviewText = forReview.length ? `${forReview.length} For Review`:''
            const rejectedText = rejected.length ? `${rejected.length} Rejected`:''
            const approvedText = approved.length ? `${approved.length} Approved`:''
            
            return {
                data: data,
                createdText: createdText,
                forReviewText: forReviewText,
                rejectedText: rejectedText,
                approvedText: approvedText
            }
        },
        viewDetails(data) {
            this.$router.push(`/settings/holidays/${this.fiscalPeriodId}/${data.holidayId}`)
        },
        async syncNew() {
            try {
                this.loading_sync = true
                await holidaysApi.sync(this.fiscal_period.fiscalPeriodId)
                this.getHolidays()
                this.loading_sync = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        viewNew() {
            this.$refs.newHolidayRef.setParams(this.holidays_new, this.fiscal_period)
            this.dialogNew = true
        },
        showAdd() {
            this.$refs.addEditHolidayRef.setParamsAdd(this.fiscal_period)
            this.dialogAddEdit = true
        },  
        async getHolidays(show_loading){
            try{
                this.loading = show_loading
                const response = await holidaysApi.getByCompany(this.fiscalPeriodId)
                if (response && response.status == 200) {
                    this.holidays_list = response.data.items
                    this.holidays_list_filtered = response.data.items
                    this.holidays_new = response.data.newItems
                    this.fiscal_period = response.data.fiscalPeriod

                    var monthYearArr = []
                    this.holidays_list.forEach(item => {
                        monthYearArr.push({
                            monthYear: this.$moment(item.date).format('MMMM YYYY')
                        })
                    })
                    this.holidays_months = Array.from(new Set(monthYearArr.map(x => x.monthYear)))
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        filterData() {    
            this.holidays_list_filtered = this.holidays_list.filter(item => {            
              return ( item.description.toLowerCase().includes(this.search.toLowerCase()) ||
                        item.date.includes(this.search)
                        )
            })

            var monthYearArr = []
            this.holidays_list_filtered.forEach(item => {
                monthYearArr.push({
                    monthYear: this.$moment(item.date).format('MMMM YYYY')
                })
            })
            this.holidays_months = Array.from(new Set(monthYearArr.map(x => x.monthYear)))
        },        
        getHolidayTypeDesc(type){
            return this.holiday_types.filter(item => {
                return item.type == type
            })[0].Description
        },
        syncHandler() {
            this.getHolidays()
        },
        updateHandler(data) {
            //exclude the the device/user who initiates the update
            if (data!=this.$store.state.userId) this.getHolidays(false)

            //invoke updateHistoryRef appendData 
            this.$refs.updateHistoryRef.getHistory()
        }
    },
    async created() {
        await this.getHolidays(true)

        //update header titles
        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: '',
            headerPageTitle: `Holidays ${this.fiscal_period.fiscalYear}`
        })
    },
    watch: {
        search(newVal) {
            updateSearchKeyword(this.$route.name, newVal)
        }
    },
    computed: {
        pageQueryId() {
            return `holidays${this.fiscalPeriodId}`
        },
        hasAddRights() {
            return hasAddRights(this.$router.currentRoute.path)
        },
        hasEditRights() {
            return hasEditRights(this.$router.currentRoute.path)
        },
        fiscalPeriodId() {
            return this.$route.params.fiscalPeriodId
        },
        commentsData() {
            if (!this.loading) {
                return {
                    pageQueryId: this.pageQueryId,
                    pageItemCreatedBy: null,
                    pageLink: this.$router.currentRoute.path,
                    pageKey: pageKeys.holidays,
                    notificationTypeId: notificationTypes.holiday_summary
                }
            }
        },
        hasHoliday() {
            return this.holidays_list.length > 0
        },
        hasNewSystemGenerated() {
            return this.holidays_new.length>0
        },
    }
}
</script>
<style scoped>
.section-layout {
    padding:0px 20px 0px 20px;
}
</style>